/**
 * Sandbox background jobs — the in-process twin of `ShellJobRegistry` (src/tools.shell.ts), but for work
 * that runs *inside* the sandbox rather than as an OS child process. Backs `bash({background:true})` and
 * (later) background sub-agents.
 *
 * IMPORTANT — this is concurrency via async-I/O interleaving, NOT parallelism. The runtime is single
 * threaded: a CPU-bound JS command still blocks. Real overlap only happens when a job *awaits* — i.e. a
 * remote VFS backend (BodDbFilesystem, network per read) or a sub-agent (model calls). Over a pure in-mem
 * MemFilesystem, backgrounding a fast command buys nothing (the tool description says so).
 *
 * The registry is kind-AGNOSTIC: it tracks any async producer's lifecycle + tail output. Overlay isolation
 * and commit-on-success are the *producer's* responsibility (IoC), so the registry stays a pure tracker.
 */
export type JobStatus = 'running' | 'done' | 'error' | 'killed';

/** A producer: does the actual work, may stream progress via `onChunk`, returns its final text. */
export interface JobFn {
  (ctx: { signal: AbortSignal; onChunk: (s: string) => void }): Promise<string>;
}

interface Job {
  kind: string;
  label: string;
  status: JobStatus;
  buf: string;
  ctl: AbortController;
  promise: Promise<void>;
}

/** Per-session registry of in-process background jobs. Bounded tail buffer; abortable; drainable at turn end. */
export class SandboxJobRegistry {
  private jobs = new Map<string, Job>();
  private seq = 0;
  constructor(private maxBuffer = 256 * 1024) {}

  /** Start `fn` in the background and return a job id immediately (does not await completion). */
  start(fn: JobFn, opts: { kind?: string; label?: string } = {}): string {
    const id = `job-${++this.seq}`;
    const ctl = new AbortController();
    const onChunk = (s: string) => { job.buf = (job.buf + s).slice(-this.maxBuffer); }; // ring: keep the tail
    const job: Job = { kind: opts.kind ?? 'task', label: opts.label ?? '', status: 'running', buf: '', ctl, promise: Promise.resolve() };
    job.promise = fn({ signal: ctl.signal, onChunk }).then(
      (res) => { if (job.status === 'running') { job.status = 'done'; if (res) onChunk(res); } },
      (e: any) => { if (job.status === 'running') { job.status = 'error'; onChunk(`\n[error] ${e?.message ?? e}`); } },
    );
    this.jobs.set(id, job);
    return id;
  }

  /** Tail output for a job (null = no such job, '' = job exists but no output yet). */
  output(id: string): string | null { return this.jobs.get(id)?.buf ?? (this.jobs.has(id) ? '' : null); }

  status(id: string): { status: JobStatus; bytes: number } | null {
    const j = this.jobs.get(id);
    return j ? { status: j.status, bytes: j.buf.length } : null;
  }

  list(): Array<{ id: string; kind: string; label: string; status: JobStatus }> {
    return [...this.jobs].map(([id, j]) => ({ id, kind: j.kind, label: j.label, status: j.status }));
  }

  /** Signal cancel (the producer decides what abort means — e.g. skip the overlay commit). */
  kill(id: string): boolean {
    const j = this.jobs.get(id);
    if (!j) return false;
    if (j.status === 'running') { j.ctl.abort(); j.status = 'killed'; }
    return true;
  }

  killAll(): void { for (const id of this.jobs.keys()) this.kill(id); }

  /** Await every still-running job. Called at turn end so a job's committed writes aren't lost from view
   *  just because the model didn't poll it. Returns the ids that were still running when drain began. */
  async drain(): Promise<string[]> {
    const pending = [...this.jobs].filter(([, j]) => j.status === 'running').map(([id]) => id);
    await Promise.all([...this.jobs.values()].map((j) => j.promise));
    return pending;
  }
}

const NO_JOB = (id: string) =>
  `Error: no background job '${id}'. Use JobStatus with no id to list jobs, or start one with bash({background:true}).`;

/** Companion tools (JobOutput / JobStatus / JobKill) over a SandboxJobRegistry. */
export function makeJobTools(registry: SandboxJobRegistry): import('./tools').AgentTool[] {
  return [
    {
      name: 'JobOutput',
      description: 'Read the accumulated output (tail) of a background job by id (from bash({background:true})).',
      parameters: { type: 'object', required: ['id'], properties: { id: { type: 'string' } } },
      async run({ id }) {
        const out = registry.output(String(id));
        if (out == null) return NO_JOB(String(id));
        const st = registry.status(String(id))!;
        return `[${st.status}]\n${out.replace(/\n+$/, '') || '(no output yet)'}`;
      },
    },
    {
      name: 'JobStatus',
      description: 'Status of a background job (running/done/killed/error). Omit `id` to list all jobs.',
      parameters: { type: 'object', properties: { id: { type: 'string' } } },
      async run({ id }) {
        if (!id) {
          const jobs = registry.list();
          return jobs.length ? jobs.map((j) => `${j.id}  ${j.status}  ${j.kind}${j.label ? '  ' + j.label : ''}`).join('\n') : '(no background jobs)';
        }
        const st = registry.status(String(id));
        return st ? `${st.status} · ${st.bytes} byte(s) buffered` : NO_JOB(String(id));
      },
    },
    {
      name: 'JobKill',
      description: 'Stop a running background job by id (the job is cancelled; its overlay writes are NOT committed).',
      parameters: { type: 'object', required: ['id'], properties: { id: { type: 'string' } } },
      async run({ id }) {
        return registry.kill(String(id)) ? `Killed job ${id}.` : NO_JOB(String(id));
      },
    },
  ];
}
